import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, switchMap } from 'rxjs';

import { AgentServiceBase, ConversationMessage } from './agent-service-base';
import { AiGeneralAgentService } from './ai-general-agent.service';
import { AgentUsage } from './agent-relay.client';
import { AgentTurn, AgentTurnFeedback } from 'src/app/@shared/models/ai/agent-turn.model';

export { ConversationMessage } from './agent-service-base';
export { AgentUsage } from './agent-relay.client';

/**
 * Façade IHM de l'assistant IA (mode relay).
 *
 * Proxifie le fil et l'état de l'agent actif vers la page assistant. La structure
 * multi-agents (registre + agent actif) est conservée pour accueillir de futurs
 * agents spécialisés ; le PoC n'expose que l'agent général.
 */
@Injectable({
    providedIn: 'root'
})
export class AiAssistantService {

    private _generalAgent = inject(AiGeneralAgentService);

    /** Registre des agents spécialisés, peuplé par chaque service via registerAgent(). */
    private _agents = new Map<string, AgentServiceBase>();

    /** Agent actif. Initialisé sur l'agent général. */
    private _activeAgent$ = new BehaviorSubject<AgentServiceBase>(this._generalAgent);

    constructor() {
        this._forwardActiveAgentObservables();
    }

    /*----------------------------------------------------------------------------------------
     * Observables — proxifiés depuis l'agent actif
     ----------------------------------------------------------------------------------------*/

    private _conversationMessages$ = new BehaviorSubject<ConversationMessage[]>([]);
    public readonly conversationMessages$ = this._conversationMessages$.asObservable();

    private _streaming$ = new BehaviorSubject<boolean>(false);
    public readonly streaming$ = this._streaming$.asObservable();

    private _statusText$ = new BehaviorSubject<string | null>(null);
    public readonly statusText$ = this._statusText$.asObservable();

    private _sessionId$ = new BehaviorSubject<string | null>(null);
    public readonly sessionId$ = this._sessionId$.asObservable();

    private _sessionUsage$ = new BehaviorSubject<AgentUsage | null>(null);
    public readonly sessionUsage$ = this._sessionUsage$.asObservable();

    public get sessionId(): string | null {
        return this._activeAgent$.value.sessionId;
    }

    /*----------------------------------------------------------------------------------------
     * Contrôles de conversation — délégués à l'agent actif
     ----------------------------------------------------------------------------------------*/

    public sendText(text: string): void {
        this._activeAgent$.value.sendText(text);
    }

    public newConversation(): void {
        this._activeAgent$.value.clear();
    }

    public setTurnFeedback(turnIndex: number, feedback: AgentTurnFeedback | null, feedbackComment: string): void {
        this._activeAgent$.value.setTurnFeedback(turnIndex, feedback, feedbackComment);
    }

    public loadSession(id: string, turns: AgentTurn[], sessionUsage: AgentUsage | null = null): void {
        this._activeAgent$.value.loadSession(id, turns, sessionUsage);
    }

    /*----------------------------------------------------------------------------------------
     * Enregistrement des agents spécialisés
     ----------------------------------------------------------------------------------------*/

    /**
     * Appelé par chaque service spécialisé dans son constructeur pour s'enregistrer.
     * Exemple : registerAgent('client_creation', this)
     */
    public registerAgent(name: string, agent: AgentServiceBase): void {
        this._agents.set(name, agent);
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _forwardActiveAgentObservables(): void {
        // Quand l'agent actif change, on re-branche les observables publics dessus
        this._activeAgent$.pipe(switchMap(agent => agent.conversationMessages$))
            .subscribe(messages => this._conversationMessages$.next(messages));

        this._activeAgent$.pipe(switchMap(agent => agent.streaming$))
            .subscribe(streaming => this._streaming$.next(streaming));

        this._activeAgent$.pipe(switchMap(agent => agent.statusText$))
            .subscribe(status => this._statusText$.next(status));

        this._activeAgent$.pipe(switchMap(agent => agent.sessionId$))
            .subscribe(id => this._sessionId$.next(id));

        this._activeAgent$.pipe(switchMap(agent => agent.sessionUsage$))
            .subscribe(usage => this._sessionUsage$.next(usage));
    }
}
